import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
} from 'chart.js';
import { Doughnut, Bar, Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
);

// Color palette matching the app's theme
export const CHART_COLORS = {
  primary: ['#1e40af', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe'],
  success: ['#15803d', '#16a34a', '#22c55e', '#4ade80', '#86efac'],
  warning: ['#a16207', '#ca8a04', '#eab308', '#facc15', '#fde047'],
  danger: ['#b91c1c', '#dc2626', '#ef4444', '#f87171', '#fca5a5'],
  neutral: ['#374151', '#4b5563', '#6b7280', '#9ca3af', '#d1d5db'],
  mixed: ['#1e40af', '#15803d', '#ca8a04', '#dc2626', '#7c3aed', '#0891b2', '#be185d', '#ea580c'],
};

interface DoughnutChartProps {
  labels: string[];
  data: number[];
  colors?: string[];
  title?: string;
  showLegend?: boolean;
  centerText?: string;
  centerSubtext?: string;
}

export const DoughnutChart: React.FC<DoughnutChartProps> = ({
  labels,
  data,
  colors = CHART_COLORS.mixed,
  title,
  showLegend = true,
  centerText,
  centerSubtext,
}) => {
  const chartData = {
    labels,
    datasets: [
      {
        data,
        backgroundColor: colors,
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverOffset: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        display: showLegend,
        position: 'bottom' as const,
        labels: {
          padding: 16,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            size: 11,
          },
        },
      },
      title: {
        display: !!title,
        text: title,
        font: {
          size: 14,
          weight: 'bold' as const,
        },
        padding: {
          bottom: 16,
        },
      },
      tooltip: {
        backgroundColor: '#1f2937',
        titleFont: { size: 12 },
        bodyFont: { size: 11 },
        padding: 10,
        cornerRadius: 6,
      },
    },
  };

  return (
    <div className="relative h-full w-full">
      <Doughnut data={chartData} options={options} />
      {centerText && (
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-2xl font-bold text-gray-900">{centerText}</span>
          {centerSubtext && (
            <span className="text-xs text-gray-500">{centerSubtext}</span>
          )}
        </div>
      )}
    </div>
  );
};

interface BarChartProps {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
    backgroundColor?: string;
  }>;
  title?: string;
  horizontal?: boolean;
  stacked?: boolean;
  showLegend?: boolean;
}

export const BarChart: React.FC<BarChartProps> = ({
  labels,
  datasets,
  title,
  horizontal = false,
  stacked = false,
  showLegend = true,
}) => {
  const chartData = {
    labels,
    datasets: datasets.map((ds, index) => ({
      ...ds,
      backgroundColor: ds.backgroundColor || CHART_COLORS.mixed[index % CHART_COLORS.mixed.length],
      borderRadius: 4,
      maxBarThickness: 40,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? ('y' as const) : ('x' as const),
    plugins: {
      legend: {
        display: showLegend && datasets.length > 1,
        position: 'top' as const,
        labels: {
          padding: 16,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            size: 11,
          },
        },
      },
      title: {
        display: !!title,
        text: title,
        font: {
          size: 14,
          weight: 'bold' as const,
        },
        padding: {
          bottom: 16,
        },
      },
      tooltip: {
        backgroundColor: '#1f2937',
        titleFont: { size: 12 },
        bodyFont: { size: 11 },
        padding: 10,
        cornerRadius: 6,
      },
    },
    scales: {
      x: {
        stacked,
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
      y: {
        stacked,
        grid: {
          color: '#f3f4f6',
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
    },
  };

  return <Bar data={chartData} options={options} />;
};

interface LineChartProps {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
    borderColor?: string;
    backgroundColor?: string;
    fill?: boolean;
  }>;
  title?: string;
  showLegend?: boolean;
}

export const LineChart: React.FC<LineChartProps> = ({
  labels,
  datasets,
  title,
  showLegend = true,
}) => {
  const chartData = {
    labels,
    datasets: datasets.map((ds, index) => ({
      ...ds,
      borderColor: ds.borderColor || CHART_COLORS.mixed[index % CHART_COLORS.mixed.length],
      backgroundColor: ds.backgroundColor || `${CHART_COLORS.mixed[index % CHART_COLORS.mixed.length]}20`,
      tension: 0.3,
      pointRadius: 4,
      pointHoverRadius: 6,
      fill: ds.fill ?? true,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: showLegend && datasets.length > 1,
        position: 'top' as const,
        labels: {
          padding: 16,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            size: 11,
          },
        },
      },
      title: {
        display: !!title,
        text: title,
        font: {
          size: 14,
          weight: 'bold' as const,
        },
        padding: {
          bottom: 16,
        },
      },
      tooltip: {
        backgroundColor: '#1f2937',
        titleFont: { size: 12 },
        bodyFont: { size: 11 },
        padding: 10,
        cornerRadius: 6,
        mode: 'index' as const,
        intersect: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
      y: {
        grid: {
          color: '#f3f4f6',
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false,
    },
  };

  return <Line data={chartData} options={options} />;
};

// Workflow Funnel Chart
interface FunnelChartProps {
  data: Array<{ label: string; value: number }>;
  title?: string;
}

export const WorkflowFunnelChart: React.FC<FunnelChartProps> = ({ data, title }) => {
  const maxValue = Math.max(...data.map((d) => d.value), 1);

  return (
    <div className="space-y-3">
      {title && <h4 className="text-sm font-medium text-gray-700 mb-4">{title}</h4>}
      {data.map((item, index) => (
        <div key={item.label} className="space-y-1">
          <div className="flex justify-between text-xs">
            <span className="text-gray-600 truncate pr-2">{item.label}</span>
            <span className="font-medium text-gray-900">{item.value}</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${(item.value / maxValue) * 100}%`,
                backgroundColor: CHART_COLORS.primary[index % CHART_COLORS.primary.length],
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// Progress Ring for individual metrics
interface ProgressRingProps {
  value: number;
  maxValue?: number;
  label: string;
  size?: 'sm' | 'md' | 'lg';
  color?: string;
}

export const ProgressRing: React.FC<ProgressRingProps> = ({
  value,
  maxValue = 100,
  label,
  size = 'md',
  color = '#1e40af',
}) => {
  const percentage = Math.min((value / maxValue) * 100, 100);
  const sizes = {
    sm: { container: 'w-16 h-16', stroke: 4, text: 'text-sm' },
    md: { container: 'w-24 h-24', stroke: 6, text: 'text-lg' },
    lg: { container: 'w-32 h-32', stroke: 8, text: 'text-xl' },
  };
  const { container, stroke, text } = sizes[size];
  const radius = 50 - stroke;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className={`relative ${container}`}>
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={stroke}
          />
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`font-bold text-gray-900 ${text}`}>{Math.round(percentage)}%</span>
        </div>
      </div>
      <span className="text-xs text-gray-600 text-center">{label}</span>
    </div>
  );
};
